import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { saveAnswer, clearAnswer, clearAllAnswers } from 'ducks/test360/reducer'
import { selectAnswers, selectTest360Competences, selectTest360EvaluatedUsers } from 'ducks/test360/selectors'
import { PersonToEvaluate } from '../types'
import { useTest360Data } from './useTest360Data'
import test360CreateAnswersDraft, { Test360AnswersBody } from 'services/process/test360/test360CreateAnswersDraft'
import { userTestIdSelector } from 'ducks/evaluatedUser'

export const useTest360Answers = () => {
  const dispatch = useDispatch()
  const rawAnswers = useSelector(selectAnswers)
  const competences = useSelector(selectTest360Competences)
  const apiEvaluatedUsers = useSelector(selectTest360EvaluatedUsers)
  const userTestId = useSelector(userTestIdSelector)
  const { evaluatedUsers } = useTest360Data()

  // Memoize answers to ensure it's always an object and prevent re-renders
  const answers = useMemo(() => rawAnswers || {}, [rawAnswers])

  // Helper function to build API body with all saved answers
  const buildAllAnswersBody = useCallback((): Test360AnswersBody => {
    const test360Array: Array<{
      competency_id: number
      questions: Array<{
        question_id: number
        answers: Array<{
          assignment_id: number
          response_weight: number
        }>
      }>
    }> = []

    // Iterate through all saved answers in Redux
    Object.keys(answers).forEach((competenceIdStr) => {
      const competenceId = parseInt(competenceIdStr, 10)
      const competence = competences.find((comp: any) => comp.competenceId === competenceId)

      if (!competence) return

      const competenceAnswers = answers[competenceId]
      if (!competenceAnswers || typeof competenceAnswers !== 'object') return

      const questionsArray: Array<{
        question_id: number
        answers: Array<{
          assignment_id: number
          response_weight: number
        }>
      }> = []

      // Iterate through questions in this competence
      Object.keys(competenceAnswers).forEach((questionIndexStr) => {
        const questionIndex = parseInt(questionIndexStr, 10)
        const questionAnswers = competenceAnswers[questionIndex]

        if (!questionAnswers || typeof questionAnswers !== 'object') return

        const question = competence.questions[questionIndex]
        if (!question) return

        const answersArray: Array<{
          assignment_id: number
          response_weight: number
        }> = []

        // Iterate through evaluated users for this question
        Object.keys(questionAnswers).forEach((evaluatedUserId) => {
          const score = questionAnswers[evaluatedUserId]
          if (score === undefined || score === null) return

          // Find the evaluated user data
          const evaluatedUser = apiEvaluatedUsers.find((user: any) => user.assignment_id.toString() === evaluatedUserId)
          if (!evaluatedUser) return

          answersArray.push({
            assignment_id: evaluatedUser.assignment_id,
            response_weight: score,
          })
        })

        // Only add question if it has answers
        if (answersArray.length > 0) {
          questionsArray.push({
            question_id: question.questionId,
            answers: answersArray,
          })
        }
      })

      // Only add competence if it has questions with answers
      if (questionsArray.length > 0) {
        test360Array.push({
          competency_id: competenceId,
          questions: questionsArray,
        })
      }
    })

    return { test360: test360Array }
  }, [answers, competences, apiEvaluatedUsers])

  // Get the list of people that need to be evaluated by the current user
  const getPeopleToEvaluate = useCallback((): PersonToEvaluate[] => {
    try {
      // Use converted data from API
      if (evaluatedUsers && Array.isArray(evaluatedUsers) && evaluatedUsers.length > 0) {
        const filteredUsers = evaluatedUsers.filter((user) => user.state !== 'closed')

        // Sort users: self-evaluation first, then others
        return filteredUsers.sort((a, b) => {
          // If 'a' is self-evaluation and 'b' is not, 'a' comes first
          if (a.isSelfEvaluation && !b.isSelfEvaluation) return -1
          // If 'b' is self-evaluation and 'a' is not, 'b' comes first
          if (!a.isSelfEvaluation && b.isSelfEvaluation) return 1
          // Otherwise, maintain original order
          return 0
        })
      }

      return []
    } catch (error) {
      console.error('Error in getPeopleToEvaluate:', error)
      return []
    }
  }, [evaluatedUsers])

  const saveUserAnswer = useCallback(
    async (competenceId: number, questionIndex: number, evaluatedUserId: string, score: number) => {
      try {
        if (!competenceId || typeof competenceId !== 'number') {
          console.error('Invalid competenceId in saveUserAnswer:', competenceId)
          return
        }

        if (questionIndex === undefined || questionIndex === null || typeof questionIndex !== 'number') {
          console.error('Invalid questionIndex in saveUserAnswer:', questionIndex)
          return
        }

        if (!evaluatedUserId || typeof evaluatedUserId !== 'string') {
          console.error('Invalid evaluatedUserId in saveUserAnswer:', evaluatedUserId)
          return
        }

        if (score === undefined || score === null || typeof score !== 'number') {
          console.error('Invalid score in saveUserAnswer:', score)
          return
        }

        // Save to Redux only
        dispatch(saveAnswer({ competenceId, questionIndex, evaluatedUserId, score }))
      } catch (error) {
        console.error('Error in saveUserAnswer:', error)
      }
    },
    [dispatch]
  )

  const saveAllAnswersToAPI = useCallback(async () => {
    try {
      const body = buildAllAnswersBody()
      if (body.test360.length === 0) return
      await test360CreateAnswersDraft(userTestId, body)
    } catch (apiError) {
      console.error('Error saving all answers to API:', apiError)
      throw apiError
    }
  }, [buildAllAnswersBody, userTestId])

  const clearUserAnswer = useCallback(
    (competenceId: number, questionIndex: number, evaluatedUserId: string) => {
      dispatch(clearAnswer({ competenceId, questionIndex, evaluatedUserId }))
    },
    [dispatch]
  )

  const clearAllUserAnswers = useCallback(() => {
    dispatch(clearAllAnswers())
  }, [dispatch])

  const getCompetenceAnswers = useCallback(
    (competenceId: number) => {
      if (!answers || !competenceId) {
        return {}
      }

      const competenceAnswers = answers[competenceId]
      return competenceAnswers && typeof competenceAnswers === 'object' ? competenceAnswers : {}
    },
    [answers]
  )

  const getQuestionAnswers = useCallback(
    (competenceId: number, totalQuestions: number) => {
      try {
        const result: Record<number, Record<string, number>> = {}

        for (let i = 0; i < totalQuestions; i++) {
          result[i] = {}
        }

        if (!answers || typeof answers !== 'object') {
          return result
        }

        if (!competenceId || typeof competenceId !== 'number') {
          return result
        }

        if (!totalQuestions || typeof totalQuestions !== 'number' || totalQuestions < 0) {
          return result
        }

        const competenceAnswers = answers[competenceId]

        const safeCompetenceAnswers =
          competenceAnswers && typeof competenceAnswers === 'object' ? competenceAnswers : {}

        for (let i = 0; i < totalQuestions; i++) {
          const questionAnswers = safeCompetenceAnswers[i]
          if (questionAnswers && typeof questionAnswers === 'object') {
            result[i] = questionAnswers
          }
        }

        return result
      } catch (error) {
        console.error('Error in getQuestionAnswers:', error)

        const result: Record<number, Record<string, number>> = {}
        for (let i = 0; i < (totalQuestions || 0); i++) {
          result[i] = {}
        }
        return result
      }
    },
    [answers]
  )

  const getUserAnswer = useCallback(
    (competenceId: number, questionIndex: number, evaluatedUserId: string) => {
      try {
        if (!answers || typeof answers !== 'object') {
          return undefined
        }

        if (!competenceId || typeof competenceId !== 'number') {
          return undefined
        }

        if (questionIndex === undefined || questionIndex === null || typeof questionIndex !== 'number') {
          return undefined
        }

        if (!evaluatedUserId || typeof evaluatedUserId !== 'string') {
          return undefined
        }

        const competenceAnswers = answers[competenceId]
        if (!competenceAnswers || typeof competenceAnswers !== 'object') {
          return undefined
        }

        const questionAnswers = competenceAnswers[questionIndex]
        if (!questionAnswers || typeof questionAnswers !== 'object') {
          return undefined
        }

        return questionAnswers[evaluatedUserId]
      } catch (error) {
        console.error('Error in getUserAnswer:', error)
        return undefined
      }
    },
    [answers]
  )

  const getCompetenceProgress = useCallback(
    (competenceId: number, totalQuestions: number) => {
      if (!answers || !competenceId || !totalQuestions) {
        return {
          answeredQuestions: 0,
          isCompleted: false,
        }
      }

      const competenceAnswers = answers[competenceId] || {}
      let answeredQuestions = 0

      const peopleToEvaluate = getPeopleToEvaluate()

      if (!peopleToEvaluate || peopleToEvaluate.length === 0) {
        return {
          answeredQuestions: 0,
          isCompleted: false,
        }
      }

      for (let i = 0; i < totalQuestions; i++) {
        const questionAnswers = competenceAnswers[i] || {}

        const allPeopleEvaluated = peopleToEvaluate.every((person: PersonToEvaluate) => {
          if (!person || !person.id) {
            return false
          }
          return questionAnswers[person.id] !== undefined
        })

        if (allPeopleEvaluated) {
          answeredQuestions++
        }
      }

      return {
        answeredQuestions,
        isCompleted: answeredQuestions === totalQuestions,
      }
    },
    [answers, getPeopleToEvaluate]
  )

  const getAnsweredQuestionsCount = useCallback(
    (competenceId: number, totalQuestions: number) => {
      const progress = getCompetenceProgress(competenceId, totalQuestions)
      return progress.answeredQuestions
    },
    [getCompetenceProgress]
  )

  const isCompetenceCompleted = useCallback(
    (competenceId: number, totalQuestions: number) => {
      const progress = getCompetenceProgress(competenceId, totalQuestions)
      return progress.isCompleted
    },
    [getCompetenceProgress]
  )

  const isQuestionFullyAnswered = useCallback(
    (competenceId: number, questionIndex: number) => {
      if (!answers || !competenceId || questionIndex === undefined || questionIndex === null) {
        return false
      }

      const questionAnswers = answers[competenceId]?.[questionIndex] || {}
      const peopleToEvaluate = getPeopleToEvaluate()

      if (!peopleToEvaluate || peopleToEvaluate.length === 0) {
        return false
      }

      return peopleToEvaluate.every((person: PersonToEvaluate) => {
        if (!person || !person.id) {
          return false
        }
        return questionAnswers[person.id] !== undefined
      })
    },
    [answers, getPeopleToEvaluate]
  )

  const canProceedToNextQuestion = useCallback(
    (competenceId: number, questionIndex: number) => {
      return isQuestionFullyAnswered(competenceId, questionIndex)
    },
    [isQuestionFullyAnswered]
  )

  return {
    saveUserAnswer,
    saveAllAnswersToAPI,
    clearUserAnswer,
    clearAllUserAnswers,
    getCompetenceAnswers,
    getQuestionAnswers,
    getUserAnswer,
    getCompetenceProgress,
    getAnsweredQuestionsCount,
    isCompetenceCompleted,
    getPeopleToEvaluate,
    isQuestionFullyAnswered,
    canProceedToNextQuestion,
    answers,
  }
}
